import { create } from 'zustand';
import { getDomain, clearCurrencyCache } from '@/lib/utils';
import { cacheStoreLogo, cacheStoreColors } from '@/utils/logoCache';

interface StoreInfo {
  site_name: string;
  slogan: string;
  site_logo: string;
  about_us: string;
  contact_email: string;
  phone_num: string;
  shop_address: string;
  shop_ig: string;
  shop_wa: string;
  shop_tg: string;
  google_map: string;
  whatsFloat: number;
  currenCy?: string;
  currency?: string;
  currency_symbol?: string;
  checkout_note: string;
  // Bank transfer fields
  m_bank: string;
  m_ac_name: string;
  m_ac_num: string;
  mb_active: number;
  // Paystack fields
  pays_pk: string;
  pays_active: number;
  // Monnify fields
  monify_api: string;
  monify_active: number;
  // Stripe fields
  stripeActive: number;
  StripeKey: string;
  StripeSecrete: string;
  // Split payment fields
  sepShipping: number;
  sepShippingAcn: string;
  heroSlider: string[];
  site_lock?: number;
  id?: number;
  // Color theme fields
  site_color?: string;
  site_bg_color?: string;
  site_text_color?: string;
  site_button_color?: string;
  site_button_text_color?: string;
}

interface StoreState {
  storeInfo: StoreInfo | null;
  setStoreInfo: (info: StoreInfo) => void;
  fetchStoreInfoByDomain: (domain: string) => Promise<void>;
  error: string | null;
  setError: (error: string | null) => void;
  // site lock modal helpers
  lockNoticeOpen: boolean;
  openLockNotice: () => void;
  closeLockNotice: () => void;
}

export const useStoreInfo = create<StoreState>((set) => ({
  storeInfo: (window as any).__INITIAL_STATE__ || null,
  error: null,
  // lock modal defaults
  lockNoticeOpen: false,
  openLockNotice: () => set({ lockNoticeOpen: true }),
  closeLockNotice: () => set({ lockNoticeOpen: false }),
  setError: (error) => set({ error }),
  setStoreInfo: (info) => {
    set({ storeInfo: info });
    
    // Clear currency cache to force refresh on next formatPrice call
    clearCurrencyCache();
    
    // Store currency - handle both 'currency' and 'currenCy' (typo variants)
    const currency = info.currency || info.currenCy || 'NGN';
    localStorage.setItem('currency', currency);
    localStorage.setItem('currency_symbol', info.currency_symbol || currency);
    
    // Cache logo if available
    if (info.site_logo) {
      cacheStoreLogo(info.site_logo, info.site_name || 'Store');
    }
    
    // Cache colors - default to gold theme
    const colors = {
      primary: (info as any).site_color || '#d4af37',
      background: (info as any).site_bg_color || '#ffffff',
      text: (info as any).site_text_color || '#000000',
      button: (info as any).site_button_color || '#d4af37',
      buttonText: (info as any).site_button_text_color || '#ffffff'
    };
    cacheStoreColors(colors.primary, colors.background, colors.text, colors.button, colors.buttonText);
  },
  fetchStoreInfoByDomain: async (domain: string) => {
    try {
      if ((window as any).__INITIAL_STATE__) {
        set({ storeInfo: (window as any).__INITIAL_STATE__ });
        return;
      }

      const cachedInfo = localStorage.getItem('storeInfo');
      if (cachedInfo) {
        const parsedInfo = JSON.parse(cachedInfo);
        set({ storeInfo: parsedInfo });
      }

      const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;
      if (!apiEndpoint) {
        throw new Error('API endpoint not configured');
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${apiEndpoint}/api/v1/store/store-info?domain=${domain}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to connect to server. Please check your connection and try again.');
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Invalid response from server. Please try again.');
      }

      const data = await response.json();
      if (!data.status) {
        throw new Error(data.message || 'Store information not found. Please try again.');
      }

      const storeInfo = {
        ...data.store,
        heroSlider: data.heroSlider || [],
      };

      localStorage.setItem('storeInfo', JSON.stringify(storeInfo));
      set({ storeInfo, error: null });

      return storeInfo;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load store information. Please refresh the page.';
      set({ error: errorMessage });
      console.error('Store info fetch error:', error);
      
      const cachedInfo = localStorage.getItem('storeInfo');
      if (cachedInfo) {
        const parsedInfo = JSON.parse(cachedInfo);
        set({ storeInfo: parsedInfo });
      }
    }
  }
}));

export async function fetchStoreInfo() {
  const { fetchStoreInfoByDomain } = useStoreInfo.getState();
  const domain = getDomain();
  return fetchStoreInfoByDomain(domain);
}

